import {Component, OnDestroy, ViewEncapsulation} from '@angular/core';
import {Shared} from "../../shared/shared";
import {AiService} from "../../../service/AiService";
import {ButtonComponent} from "../generic/table/buttonComponent/buttonComponent";
import {CourseService} from "../../../service/CourseService";
import {AiGeneratedFlashcardDto} from "../../../dto/AiGeneratedFlashcardDto";
import {AiGenerationPlanDto} from "../../../dto/AiGenerationPlanDto";
import {AiGenerationJobDto} from "../../../dto/AiGenerationJobDto";
import {CourseEntity} from "../../../model/CourseEntity";

/**
 * Flashcards generadas por IA: se piden aqui y se revisan aqui. Es la gemela de aiQuestions y
 * funciona igual, porque el ciclo es el mismo.
 *
 * El gestor pide lo que quiere en la barra de arriba ("dame 10 flashcards de cada tema de
 * Cardiologia"), la peticion se interpreta y se le ensena el plan con el coste ANTES de gastar
 * nada; al confirmar, la generacion corre en segundo plano y va cayendo en la cola de pendientes.
 *
 * La cola solo tiene lo que no ha revisado nadie. Aceptar marca human_reviewed y la tarjeta pasa a
 * ser una mas de la baraja, servible a cualquier alumno tanto por el chat como por la pantalla de
 * flashcards. Rechazar la BORRA.
 *
 * La vista previa muestra la tarjeta con sus dos caras a la vez, no girandola: quien revisa
 * necesita ver anverso y reverso juntos para juzgar si la respuesta cierra la pregunta.
 *
 * Permisos: AI_FLASHCARDS_REVIEW para aceptar y rechazar, AI_FLASHCARDS_GENERATE para pedir (va
 * aparte porque cada peticion gasta dinero real), AI_INDEX para el mantenimiento del indice.
 */
@Component({
  selector: 'app-ai-flashcards',
  templateUrl: 'aiFlashcards.html',
  styleUrls: ['aiFlashcards.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AiFlashcards implements OnDestroy {

  segment: 'pending' | 'reviewed' = 'pending';
  pending: AiGeneratedFlashcardDto[] = [];
  reviewed: AiGeneratedFlashcardDto[] = [];
  selected: AiGeneratedFlashcardDto = null;
  /**
   * Copia de trabajo mientras se edita. Se edita una copia y no la fila de la lista para que
   * cancelar sea de verdad cancelar y no dejar en pantalla algo que no esta guardado.
   */
  editando: AiGeneratedFlashcardDto = null;
  guardando = false;
  loading = false;
  showTable = true;
  /** Ultimo fallo de carga, para no dejar la pantalla muda cuando el backend no responde. */
  error: string = null;

  // Barra de peticion
  courses: CourseEntity[] = [];
  courseId: number = null;
  requestText = '';
  plan: AiGenerationPlanDto = null;
  planning = false;
  /** Para no pedir el coste en cada pulsacion mientras se ajustan los numeros. */
  private estimarTimeout: any = null;
  launching = false;

  jobs: AiGenerationJobDto[] = [];
  /** Los que se pintan. Se calcula al recibirlos y NO en el template: una funcion que devuelve
   *  un array nuevo en cada ciclo de deteccion de cambios hace que *ngFor se repinte sin parar. */
  jobsVisibles: AiGenerationJobDto[] = [];
  private jobsTimer: any = null;

  headers: any = {
    columns: {
      subjectName: {title: 'Asignatura', hide: false, filter: false},
      topicName: {title: 'Tema', hide: false, filter: false},
      keyPoint: {
        title: 'Concepto', hide: false, filter: false,
        valuePrepareFunction: (keyPoint: string) => {
          return keyPoint == null ? '' : keyPoint;
        }
      },
      statement: {
        title: 'Anverso', hide: false, filter: false,
        valuePrepareFunction: (statement: string) => {
          return statement == null ? '' : statement.substring(0, 90);
        }
      },
      difficulty: {title: 'Dif.', hide: false, filter: false},
      // Con las casillas encendidas, pinchar la fila ya no abre nada (es de ng2-smart-table), asi
      // que abrir pasa a ser un boton.
      id: {
        title: '', hide: false, filter: false, type: 'custom',
        valuePrepareFunction: (cell, row) => {
          return {cell: row.id, icon: 'open-outline', text: 'Abrir', color: '#1c8aa5'};
        },
        renderComponent: ButtonComponent,
        onComponentInitFunction: (instance) => {
          if (instance != null) {
            instance.changeButton.subscribe(id => this.abrirPorId(id));
          }
        },
      },
      reference: {title: 'Reference', hide: true, filter: false},
      answer: {title: 'Answer', hide: true, filter: false},
      created: {title: 'Created', hide: true, filter: false},
      subjectId: {title: 'Subject id', hide: true, filter: false},
      topicId: {title: 'Topic id', hide: true, filter: false},
      processing: {title: '', hide: true, filter: false}
    },
    actions: false,
  };

  constructor(public shared: Shared, private aiService: AiService, private courseService: CourseService) {
    this.load();
  }

  ngOnDestroy() {
    this.pararSeguimiento();
  }

  get canReview(): boolean {
    return this.permitido('AI_FLASHCARDS_REVIEW');
  }

  get canGenerate(): boolean {
    return this.permitido('AI_FLASHCARDS_GENERATE');
  }

  get canIndex(): boolean {
    return this.permitido('AI_INDEX');
  }

  private permitido(permiso: string): boolean {
    return this.shared.checkIsAiPermitted(permiso) === true;
  }

  load() {
    this.loading = true;
    // Todas las llamadas llevan su rama de error: sin ella, un endpoint que falle deja loading a
    // true para siempre y la pantalla se queda en blanco sin decir por que.
    this.aiService.getPendingFlashcards().subscribe(x => {
      this.pending = x == null ? [] : x;
      this.loading = false;
      this.refrescarTabla();
    }, e => {
      this.pending = [];
      this.loading = false;
      this.error = 'No se ha podido cargar la cola de flashcards (' + this.textoError(e) + ').';
    });
    this.aiService.getReviewedFlashcards().subscribe(x => {
      this.reviewed = x == null ? [] : x;
    }, () => this.reviewed = []);
    if (this.canGenerate && this.courses.length === 0) {
      this.courseService.getCourses(this.shared.CORP_ID).subscribe(x => {
        this.courses = x == null ? [] : x;
      }, () => this.courses = []);
    }
    this.cargarTrabajos();
  }

  /** Abre la ficha desde el boton de la fila. */
  abrirPorId(id: number) {
    const fila = this.list.find(x => x.id === id);
    if (fila != null) {
      this.rowSelectedEmitter(fila);
    }
  }

  get list(): AiGeneratedFlashcardDto[] {
    return this.segment === 'pending' ? this.pending : this.reviewed;
  }

  cambiarSegmento(segmento: 'pending' | 'reviewed') {
    if (!this.confirmarSalidaDeEdicion()) {
      return;
    }
    this.segment = segmento;
    this.selected = null;
    this.refrescarTabla();
  }

  /**
   * ng2-smart-table se queda con la fuente de datos con la que se pinto, asi que para que la
   * tabla refleje un cambio de lista hay que sacarla del DOM y volver a meterla.
   */
  private refrescarTabla() {
    this.showTable = false;
    setTimeout(() => this.showTable = true, 50);
  }

  rowSelectedEmitter(row: any) {
    if (row == null) {
      if (this.confirmarSalidaDeEdicion()) {
        this.selected = null;
      }
      return;
    }
    if (this.editando != null && this.editando.id === row.id) {
      // Es la que se esta editando: no se hace nada, para no tirar el borrador por un clic.
      return;
    }
    if (!this.confirmarSalidaDeEdicion()) {
      return;
    }
    // La fila que emite la tabla es una copia plana, asi que se busca el objeto real: es el que
    // lleva el estado de procesando y el que hay que quitar de la lista al resolverlo.
    this.selected = this.list.find(x => x.id === row.id);
  }

  /** El reverso viene con una linea por elemento; se parte para poder pintarlo como lista. */
  lineas(texto: string): string[] {
    if (texto == null || texto.trim() === '') {
      return [];
    }
    return texto.split('\n').map(l => l.trim()).filter(l => l !== '');
  }

  // ------------------------------------------------------------------------- edicion

  /**
   * Editar antes de resolver, igual que en las preguntas: una tarjeta que solo falla en una
   * palabra del reverso no merece tirarse y volver a generarse.
   *
   * Guardar no acepta la tarjeta: se queda donde estaba y hay que resolverla igual.
   */
  editar() {
    if (this.selected == null || !this.canReview) {
      return;
    }
    this.editando = Object.assign(new AiGeneratedFlashcardDto(), this.selected);
  }

  cancelarEdicion() {
    if (!this.confirmarSalidaDeEdicion()) {
      return;
    }
    this.editando = null;
  }

  get puedeGuardar(): boolean {
    const e = this.editando;
    if (e == null || this.guardando) {
      return false;
    }
    // Las dos caras son obligatorias: una tarjeta con una cara vacia no sirve de nada.
    return e.statement != null && e.statement.trim() !== ''
      && e.answer != null && e.answer.trim() !== '';
  }

  guardarEdicion() {
    if (!this.puedeGuardar) {
      return;
    }
    this.guardando = true;
    // Se manda solo lo que edita esta pantalla, y no la copia entera: processing es de aqui y
    // asignatura, tema y fecha no se tocan desde la revision.
    const cambios: any = {
      id: this.editando.id,
      statement: this.editando.statement,
      answer: this.editando.answer,
      difficulty: this.editando.difficulty,
      keyPoint: this.editando.keyPoint
    };
    this.aiService.updateFlashcard(cambios, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      this.guardando = false;
      if (r != null && r.ok === false) {
        this.error = r.message;
        return;
      }
      this.error = null;
      // Se vuelca lo guardado sobre la fila real de la lista, que es la que pinta la tabla y la
      // que se acabara aceptando o rechazando.
      this.volcar(r != null && r.flashcard != null ? r.flashcard : this.editando, this.selected);
      this.editando = null;
      this.refrescarTabla();
    }, e => {
      this.guardando = false;
      this.error = 'No se han podido guardar los cambios (' + this.textoError(e) + ').';
    });
  }

  /** Solo lo editable: asignatura, tema y fecha no los cambia esta pantalla. */
  private volcar(desde: AiGeneratedFlashcardDto, hacia: AiGeneratedFlashcardDto) {
    if (desde == null || hacia == null) {
      return;
    }
    hacia.statement = desde.statement;
    hacia.answer = desde.answer;
    hacia.difficulty = desde.difficulty;
    hacia.keyPoint = desde.keyPoint;
  }

  /**
   * Se llama antes de cualquier cosa que se lleve el borrador por delante. Solo pregunta si hay
   * algo escrito distinto de lo que habia.
   */
  private confirmarSalidaDeEdicion(): boolean {
    if (this.editando == null || !this.hayCambios()) {
      this.editando = null;
      return true;
    }
    if (!confirm('Hay cambios sin guardar en la flashcard. ¿Los descartas?')) {
      return false;
    }
    this.editando = null;
    return true;
  }

  private hayCambios(): boolean {
    const e = this.editando;
    const o = this.selected;
    if (e == null || o == null) {
      return false;
    }
    return e.statement !== o.statement
      || e.answer !== o.answer
      || Number(e.difficulty) !== Number(o.difficulty)
      || !this.mismoTexto(e.keyPoint, o.keyPoint);
  }

  /**
   * Un campo que llega vacio de base de datos es null, pero el textarea lo devuelve como cadena
   * vacia. Sin esto, abrir y cerrar la edicion sin tocar nada preguntaria por cambios que no hay.
   */
  private mismoTexto(a: string, b: string): boolean {
    return (a == null ? '' : a) === (b == null ? '' : b);
  }


  // ------------------------------------------------------------------------ revision en bloque

  /**
   * Lo marcado ahora mismo en la tabla. Son los mismos objetos que hay en la lista, asi que
   * comparar por id es suficiente para sacarlos de la cola despues.
   */
  seleccion: AiGeneratedFlashcardDto[] = [];
  /** Hay una accion en bloque en marcha: los dos botones se apagan mientras tanto. */
  enBloque = false;

  cambiarSeleccion(filas: AiGeneratedFlashcardDto[]) {
    this.seleccion = filas == null ? [] : filas;
  }

  get puedeEnBloque(): boolean {
    return this.canReview && this.segment === 'pending' && !this.enBloque
      && this.editando == null && this.seleccion.length > 0;
  }

  aceptarSeleccionadas() {
    if (!this.puedeEnBloque) {
      return;
    }
    const ids = this.seleccion.map(x => x.id);
    this.enBloque = true;
    this.aiService.reviewFlashcards(ids, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      this.enBloque = false;
      this.resolverBloque(ids, r, true);
    }, e => {
      this.enBloque = false;
      this.error = 'No se han podido aceptar las flashcards (' + this.textoError(e) + ').';
    });
  }

  rechazarSeleccionadas() {
    if (!this.puedeEnBloque) {
      return;
    }
    const ids = this.seleccion.map(x => x.id);
    if (!confirm('Se van a borrar ' + ids.length + ' flashcards con todo lo que cuelga de ellas. Esto no se puede deshacer.')) {
      return;
    }
    this.enBloque = true;
    this.aiService.discardFlashcards(ids, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      this.enBloque = false;
      this.resolverBloque(ids, r, false);
    }, e => {
      this.enBloque = false;
      this.error = 'No se han podido borrar las flashcards (' + this.textoError(e) + ').';
    });
  }

  /**
   * Saca de la cola solo lo que de verdad se ha resuelto.
   *
   * Los ids que el backend devuelve en failed se quedan donde estaban: una fila que desaparece sin
   * haberse borrado es peor que un aviso, porque el revisor la da por hecha.
   */
  private resolverBloque(pedidos: number[], respuesta: any, aceptadas: boolean) {
    const fallidos: number[] = respuesta != null && respuesta.failed != null ? respuesta.failed : [];
    const resueltos = pedidos.filter(id => fallidos.indexOf(id) < 0);
    const movidas = this.pending.filter(x => resueltos.indexOf(x.id) >= 0);

    this.pending = this.pending.filter(x => resueltos.indexOf(x.id) < 0);
    if (aceptadas) {
      this.reviewed = movidas.concat(this.reviewed);
    }
    this.seleccion = [];
    this.selected = null;
    this.error = fallidos.length > 0 && respuesta != null ? respuesta.message : null;
    this.refrescarTabla();
  }

  // ------------------------------------------------------------------------ revision

  approve(f: AiGeneratedFlashcardDto) {
    if (f == null || f.processing || !this.canReview || this.editando != null) {
      return;
    }
    f.processing = true;
    this.aiService.reviewFlashcard(f.id, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      f.processing = false;
      if (r != null && r.ok === false) {
        this.error = r.message;
        return;
      }
      // Se saca de la cola al momento en vez de recargar: la lista puede ser larga y recargarla
      // entera haria perder la posicion de lectura.
      this.error = null;
      this.pending = this.pending.filter(x => x.id !== f.id);
      this.reviewed.unshift(f);
      this.selected = null;
      this.refrescarTabla();
    }, e => {
      f.processing = false;
      this.error = 'No se ha podido aceptar la flashcard (' + this.textoError(e) + ').';
    });
  }

  discard(f: AiGeneratedFlashcardDto) {
    if (f == null || f.processing || !this.canReview || this.editando != null) {
      return;
    }
    if (!confirm('Se va a borrar la flashcard. Esto no se puede deshacer.')) {
      return;
    }
    f.processing = true;
    this.aiService.discardFlashcard(f.id, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      f.processing = false;
      // El servidor puede negarse a borrar. En ese caso la fila se queda donde esta y se dice por
      // que, en vez de fingir que se fue.
      if (r != null && r.ok === false) {
        this.error = r.message;
        return;
      }
      this.error = null;
      this.pending = this.pending.filter(x => x.id !== f.id);
      this.selected = null;
      this.refrescarTabla();
    }, e => {
      f.processing = false;
      this.error = 'No se ha podido borrar la flashcard (' + this.textoError(e) + ').';
    });
  }

  // ------------------------------------------------------------------ peticion de flashcards

  pedirPlan() {
    if (!this.canGenerate || this.planning || this.requestText.trim() === '') {
      return;
    }
    this.planning = true;
    this.plan = null;
    this.aiService.planFlashcardGeneration(this.courseId, this.requestText).subscribe(x => {
      this.plan = x;
      this.planning = false;
    }, () => {
      this.planning = false;
    });
  }

  /**
   * El gestor puede ajustar a mano cuantas quiere de cada tema antes de confirmar.
   *
   * El total se pinta al momento, pero el precio lo vuelve a decir el backend. Antes se
   * multiplicaba aqui por un numero fijo escrito a pelo, que ademas solo cubria la salida del
   * modelo: en cuanto se tocaba un tema, la cifra dejaba de tener que ver ni con el backend ni con
   * la factura. Y ahora la cuenta lleva dentro las imagenes y el descarte, que no caben en una
   * multiplicacion.
   */
  recalcularTotal() {
    if (this.plan == null || this.plan.items == null) {
      return;
    }
    let total = 0;
    this.plan.items.forEach(x => total += (x.num == null ? 0 : Number(x.num)));
    this.plan.total = total;

    // Se espera a que pare de escribir: cada pulsacion en el input dispara este metodo.
    if (this.estimarTimeout != null) {
      clearTimeout(this.estimarTimeout);
    }
    this.estimarTimeout = setTimeout(() => this.pedirEstimacion(), 400);
  }

  private pedirEstimacion() {
    if (this.plan == null || this.plan.total <= 0) {
      return;
    }
    this.aiService.estimateGenerationCost(this.courseId, this.plan, 'FLASHCARD').subscribe(x => {
      // Puede haber seguido tocando numeros mientras iba la peticion: solo se cogen las cifras del
      // coste, nunca los temas, que son los que tiene delante.
      if (x != null && this.plan != null) {
        this.plan.estimatedCostEur = x.estimatedCostEur;
        this.plan.textCostEur = x.textCostEur;
        this.plan.imageCostEur = x.imageCostEur;
        this.plan.imageCount = x.imageCount;
        this.plan.retryFactor = x.retryFactor;
      }
    });
  }

  quitarTema(index: number) {
    this.plan.items.splice(index, 1);
    this.recalcularTotal();
  }

  confirmarPlan() {
    if (!this.canGenerate || this.launching || this.plan == null || this.plan.total <= 0) {
      return;
    }
    // Ha pedido algo que este examen no admite. No se genera otra cosa a sus espaldas: el gestor
    // puede tocar la configuracion del curso, asi que lo que toca es que lo arregle. El backend lo
    // rechaza igualmente; esto es para que el boton no mienta.
    if (this.plan.blocked) {
      return;
    }
    this.launching = true;
    this.aiService.launchFlashcardGeneration(this.courseId, this.requestText, this.plan,
      this.shared.CURRENT_MANAGER.id).subscribe(() => {
      this.launching = false;
      this.plan = null;
      this.requestText = '';
      this.cargarTrabajos();
    }, () => {
      this.launching = false;
    });
  }

  cancelarPlan() {
    this.plan = null;
  }

  // ---------------------------------------------------------------------- avance del trabajo

  cargarTrabajos() {
    if (!this.canGenerate) {
      return;
    }
    this.aiService.getFlashcardGenerationJobs().subscribe(x => {
      this.jobs = x == null ? [] : x;
      this.calcularJobsVisibles();
      // Mientras haya algo en curso se sigue mirando, y al terminar se recarga la cola una vez
      // para ver las tarjetas nuevas.
      if (this.jobs.some(j => j.status === 'RUNNING' || j.status === 'PLANNED')) {
        this.arrancarSeguimiento();
      } else if (this.jobsTimer != null) {
        this.pararSeguimiento();
        this.load();
      }
    }, () => {
      // Si el backend todavia no tiene el endpoint de trabajos, la revision tiene que seguir
      // funcionando igual: se deja de preguntar y ya esta.
      this.jobs = [];
      this.calcularJobsVisibles();
      this.pararSeguimiento();
    });
  }

  private arrancarSeguimiento() {
    if (this.jobsTimer != null) {
      return;
    }
    this.jobsTimer = setInterval(() => this.cargarTrabajos(), 5000);
  }

  private pararSeguimiento() {
    if (this.jobsTimer != null) {
      clearInterval(this.jobsTimer);
      this.jobsTimer = null;
    }
  }

  private calcularJobsVisibles() {
    // Solo lo que se esta generando ahora. Un trabajo terminado no aporta nada: lo que produjo ya
    // esta en la cola de pendientes, que es donde hay que mirarlo, y dejar la barra al 10/10 en
    // pantalla solo ocupa sitio y confunde con una peticion nueva.
    this.jobsVisibles = this.jobs.filter(j => j.status === 'RUNNING' || j.status === 'PLANNED');
  }

  progreso(job: AiGenerationJobDto): number {
    if (job == null || job.total == null || job.total === 0) {
      return 0;
    }
    return Math.min(100, Math.round((job.generated * 100) / job.total));
  }

  private textoError(e: any): string {
    if (e == null) {
      return 'sin detalle';
    }
    return typeof e === 'string' ? e : (e.status != null ? 'HTTP ' + e.status : 'sin detalle');
  }
}
